import React, { useEffect, useMemo } from 'react'
import { useStateWithDebounce, useGroupsDataLazy, SortDirectionEnum } from '@modmd/data'
import { Dropdown, DropdownOnSearchChangeData } from 'components/AutoCompleteDropdown'

interface Props {
  companyId?: string
  onChange: (id: string) => void
  value?: string
  hasBottomContent?: boolean
  groupsInDropdown?: number
  isDisabled?: boolean
  error?: string
}

export const GroupsDropdown: React.VFC<Props> = ({
  onChange,
  companyId,
  value,
  hasBottomContent,
  groupsInDropdown = 0,
  isDisabled = false,
  error,
}) => {
  const [hasDefaultValueFromUrl, setHasDefaultValueFromUrl] = React.useState(!!value)
  const [hasValueSelected, setHasValueSelected] = React.useState(false)
  const [, setSearchQuery, debouncedSearchQuery] = useStateWithDebounce('')

  const [getGroupData, { data, loading, refetch }] = useGroupsDataLazy()

  React.useEffect(() => {
    setSearchQuery('')
    setHasDefaultValueFromUrl(false)
    setHasValueSelected(false)
    if (refetch) void refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  const handleSearchChange = React.useCallback(
    (_: React.SyntheticEvent<HTMLElement>, data: DropdownOnSearchChangeData) => {
      setSearchQuery(data.searchQuery)
      setHasDefaultValueFromUrl(false)
    },
    [setSearchQuery]
  )
  const pageLength = hasDefaultValueFromUrl ? 1 : groupsInDropdown

  const filterVariables = useMemo(
    () => ({
      filterData: hasDefaultValueFromUrl
        ? { id: value }
        : { nameLike: debouncedSearchQuery, companyIds: companyId ? [String(companyId)] : [] },
      pagination: {
        from: 1 * pageLength - pageLength,
        length: pageLength,
        sortBy: 'id',
        direction: SortDirectionEnum.DESC,
      },
      membersFilterData: {},
      membersPagination: {
        from: 1 * pageLength - pageLength,
        length: 10,
        sortBy: 'id',
        direction: SortDirectionEnum.DESC,
      },
    }),
    [companyId, debouncedSearchQuery, hasDefaultValueFromUrl, pageLength, value]
  )

  useEffect(() => {
    if (!hasValueSelected) {
      getGroupData({
        variables: filterVariables,
      })
    }
  }, [filterVariables, getGroupData, hasDefaultValueFromUrl, hasValueSelected])

  return (
    <Dropdown
      label="Group"
      value={value}
      options={
        hasDefaultValueFromUrl
          ? [{ value, text: data?.groups?.Groups?.[0].name }]
          : data?.groups?.Groups.map((group) => ({
              value: group.id,
              text: group.name,
            }))
      }
      onSelectChange={(_: React.SyntheticEvent<HTMLElement>, data: { value: string }) => {
        onChange(data.value)
        setHasValueSelected(!!data.value)
        setHasDefaultValueFromUrl(false)
      }}
      onBlur={() => setSearchQuery('')}
      onSearchChange={handleSearchChange}
      isLoading={loading}
      selectOnNavigation={false}
      hasBottomContent={hasBottomContent}
      disabled={isDisabled}
      errorMessage={error}
      clearable
      isClearable
    />
  )
}
